import React, { useState, useEffect, useMemo } from 'react';
import { db } from '@/lib/firebase';
import { collection, query, orderBy, onSnapshot, limit, doc, updateDoc, startAfter, getDocs, QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { Loader2, X, Eye, Download, ExternalLink, Image as ImageIcon, Search, Filter, ChevronDown } from 'lucide-react';
import { modalApi } from '../../services/modalApi'; // Re-use modalApi to check backend health if needed

/**
 * Full Submission interface matching new data structure
 * Reference: docs/CUP_CUSTOMIZER_DATA_STRUCTURE.md
 */
interface Submission {
    id: string;
    design_id?: string;
    product_type?: string;
    productType?: string; // Legacy field
    status: string;
    created_at?: any;
    createdAt?: any; // Legacy field
    updated_at?: any;
    
    // Image paths
    image_id?: string;
    original_image_path?: string;
    transformed_image_id?: string;
    transformed_image_path?: string;
    mockup_image_path?: string;
    
    // Design parameters
    design_options?: {
        position?: { x: number; y: number };
        scale?: number;
        rotation?: number;
        printType?: 'laser' | 'silk';
        color?: string;
    };
    designOptions?: { removeBackground: boolean }; // Legacy field
    
    // Template & Haravan
    template_id?: string;
    haravan_product_id?: string;
    haravan_variant_id?: string;
    haravan_order_id?: string;
    
    // User tracking
    visitor_id?: string;
    session_id?: string;
    
    // Billing
    billing?: {
        status: string;
        amount: number;
        currency: string;
        retry_count: number;
        charge_error?: string;
        charged_at?: any;
        last_retry_at?: any;
    };
    
    // Metadata
    metadata?: {
        description?: string;
        positionDescription?: string;
        scaleDescription?: string;
        imageUsed?: string;
    };
    
    userAgent?: string; // Legacy field
}

/**
 * Submission Detail Modal Component
 * Shows full information when admin clicks on a submission
 */
interface SubmissionDetailModalProps {
    submission: Submission;
    onClose: () => void;
    loadingImages: {[key: string]: string};
    onLoadImage: (imagePath: string, imageKey: string) => void;
}

function SubmissionDetailModal({ submission, onClose, loadingImages, onLoadImage }: SubmissionDetailModalProps) {
    const baseUrl = import.meta.env.VITE_MODAL_API_BASE_URL;

    // Helper: Convert Modal internal path to backend /get_image endpoint
    const getImageUrl = (imagePath: string): string => {
        if (!imagePath) return '';

        // If already a full URL, return as-is
        if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
            return imagePath;
        }

        // Use backend /get_image endpoint to proxy Modal Volume
        // Backend will handle CORS and serve the image
        const encodedPath = encodeURIComponent(imagePath);
        const imageUrl = `${baseUrl}/get_image?path=${encodedPath}`;

        console.log(`[AdminDashboard] Converting path: ${imagePath} => ${imageUrl}`);

        return imageUrl;
    };

    // Format timestamp
    const formatTimestamp = (timestamp: any) => {
        if (!timestamp) return 'N/A';
        if (timestamp.seconds) {
            return new Date(timestamp.seconds * 1000).toLocaleString('vi-VN');
        }
        return new Date(timestamp).toLocaleString('vi-VN');
    };

    // Render image with lazy loading
    const renderImage = (imagePath: string | undefined, imageKey: string, label: string) => {
        if (!imagePath) return null;

        const imageState = loadingImages[imageKey];
        const fullUrl = getImageUrl(imagePath);

        return (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex justify-between items-start mb-2">
                    <h4 className="text-sm font-semibold text-gray-900">{label}</h4>
                    <a 
                        href={fullUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700"
                    >
                        <ExternalLink className="w-4 h-4" />
                    </a>
                </div>
                
                <div className="relative bg-white rounded-lg overflow-hidden min-h-[200px] flex items-center justify-center border border-gray-300">
                    {!imageState ? (
                        <button
                            onClick={() => onLoadImage(imagePath, imageKey)}
                            className="flex flex-col items-center gap-2 px-4 py-3 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition-colors"
                        >
                            <ImageIcon className="w-8 h-8" />
                            <span className="text-sm font-medium">Nhấn để xem ảnh</span>
                        </button>
                    ) : imageState === 'loading' ? (
                        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                    ) : imageState === 'error' ? (
                        <div className="flex flex-col items-center gap-2 text-center p-4">
                            <div className="text-red-500 text-sm font-medium">Không thể tải ảnh</div>
                            <div className="text-xs text-gray-500">File có thể đã bị xóa hoặc không tồn tại</div>
                            <button
                                onClick={() => onLoadImage(imagePath, imageKey)}
                                className="mt-2 px-3 py-1 text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 rounded transition-colors"
                            >
                                Thử lại
                            </button>
                        </div>
                    ) : (
                        <img
                            src={imageState}
                            alt={label}
                            className="max-w-full max-h-[400px] object-contain"
                        />
                    )}
                </div>
                
                <div className="mt-2 text-xs text-gray-500 font-mono break-all">
                    {imagePath}
                </div>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full my-8 max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
                    <h2 className="text-xl font-bold text-gray-900">
                        Chi tiết thiết kế: {submission.design_id || submission.id}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Basic Information */}
                    <section>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Thông tin cơ bản</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <InfoItem label="Mã thiết kế" value={submission.design_id || submission.id} />
                            <InfoItem label="Trạng thái" value={submission.status} />
                            <InfoItem label="Loại sản phẩm" value={submission.product_type || submission.productType || 'N/A'} />
                            <InfoItem label="Ngày tạo" value={formatTimestamp(submission.created_at || submission.createdAt)} />
                            <InfoItem label="Cập nhật lần cuối" value={formatTimestamp(submission.updated_at)} />
                            <InfoItem label="Mã template" value={submission.template_id || 'N/A'} />
                        </div>
                    </section>

                    {/* Design Parameters */}
                    {submission.design_options && (
                        <section>
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Thông số thiết kế</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <InfoItem
                                    label="Kiểu in"
                                    value={submission.design_options.printType === 'laser' ? '🔥 Khắc Laser' : submission.design_options.printType === 'silk' ? '🎨 In Lụa' : 'N/A'}
                                />
                                <InfoItem label="Màu sắc" value={submission.design_options.color || 'N/A'} />
                                <InfoItem
                                    label="Vị trí (từ tâm)"
                                    value={submission.design_options.position ?
                                        `X: ${submission.design_options.position.x}%, Y: ${submission.design_options.position.y}%` :
                                        'N/A'
                                    }
                                />
                                <div className="col-span-2">
                                    <InfoItem
                                        label="Kích thước (So với khung in)"
                                        value={submission.design_options.scale ? `${submission.design_options.scale}%` : 'N/A'}
                                    />
                                    {submission.design_options.scale && (
                                        <div className="mt-2">
                                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                                                <div
                                                    className="bg-blue-600 h-2.5 rounded-full transition-all"
                                                    style={{ width: `${submission.design_options.scale}%` }}
                                                ></div>
                                            </div>
                                            <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                                                <span>Nhỏ nhất</span>
                                                <span className="font-semibold text-blue-600">{submission.design_options.scale}%</span>
                                                <span>Vừa khít khung</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <InfoItem
                                    label="Xoay"
                                    value={submission.design_options.rotation !== undefined ? `${submission.design_options.rotation}°` : 'N/A'}
                                />
                            </div>

                            {/* Metadata descriptions */}
                            {submission.metadata && (
                                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                    <h4 className="text-sm font-semibold text-blue-900 mb-2">Chi tiết thiết kế</h4>
                                    <div className="space-y-1 text-sm text-blue-800">
                                        {submission.metadata.description && <p>• {submission.metadata.description}</p>}
                                        {submission.metadata.positionDescription && <p>• Vị trí: {submission.metadata.positionDescription}</p>}
                                        {submission.metadata.scaleDescription && (
                                            <div className="space-y-1">
                                                <p>• Kích thước: {submission.metadata.scaleDescription}</p>
                                                {submission.design_options?.scale && (
                                                    <p className="text-xs text-blue-700 ml-4">
                                                        → Logo chiếm <strong>{submission.design_options.scale}%</strong> chiều ngang và chiều dọc của khung in (red box)
                                                        {submission.design_options.scale === 100 && " - Vừa khít"}
                                                        {submission.design_options.scale < 50 && " - Nhỏ"}
                                                        {submission.design_options.scale >= 50 && submission.design_options.scale < 80 && " - Trung bình"}
                                                        {submission.design_options.scale >= 80 && submission.design_options.scale < 100 && " - Lớn"}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                        {submission.metadata.imageUsed && <p>• Ảnh sử dụng: {submission.metadata.imageUsed}</p>}
                                    </div>
                                </div>
                            )}
                        </section>
                    )}

                    {/* User Tracking */}
                    <section>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Theo dõi người dùng</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <InfoItem label="Mã khách" value={submission.visitor_id || 'N/A'} mono />
                            <InfoItem label="Mã phiên" value={submission.session_id || 'N/A'} mono />
                        </div>
                    </section>

                    {/* Haravan Integration */}
                    <section>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Tích hợp Haravan</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <InfoItem label="Mã sản phẩm" value={submission.haravan_product_id || 'N/A'} />
                            <InfoItem label="Mã biến thể" value={submission.haravan_variant_id || 'N/A'} />
                            <InfoItem label="Mã đơn hàng" value={submission.haravan_order_id || 'Chưa đặt hàng'} />
                        </div>
                    </section>

                    {/* Billing Information */}
                    {submission.billing && (
                        <section>
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Thông tin thanh toán</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <InfoItem label="Trạng thái" value={submission.billing.status} />
                                <InfoItem label="Số tiền" value={`${submission.billing.amount.toLocaleString()} ${submission.billing.currency}`} />
                                <InfoItem label="Số lần thử lại" value={submission.billing.retry_count.toString()} />
                                <InfoItem label="Thanh toán lúc" value={formatTimestamp(submission.billing.charged_at)} />
                                {submission.billing.charge_error && (
                                    <div className="col-span-2">
                                        <InfoItem label="Lỗi" value={submission.billing.charge_error} className="text-red-600" />
                                    </div>
                                )}
                            </div>
                        </section>
                    )}

                    {/* Images Section */}
                    <section>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Hình ảnh</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {renderImage(submission.mockup_image_path, `${submission.id}_mockup`, '🖼️ Ảnh mô phỏng')}
                            {renderImage(submission.original_image_path, `${submission.id}_original`, '📄 Ảnh gốc')}
                            {renderImage(submission.transformed_image_path, `${submission.id}_transformed`,
                                'Ảnh in'
                            )}
                        </div>

                        {/* Image IDs */}
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                            {submission.image_id && (
                                <div className="p-2 bg-gray-100 rounded">
                                    <span className="font-semibold">Mã ảnh gốc:</span> <span className="font-mono">{submission.image_id}</span>
                                </div>
                            )}
                            {submission.transformed_image_id && (
                                <div className="p-2 bg-gray-100 rounded">
                                    <span className="font-semibold">Mã ảnh xử lý:</span> <span className="font-mono">{submission.transformed_image_id}</span>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Raw JSON (for debugging) */}
                    <section>
                        <details className="group">
                            <summary className="cursor-pointer text-sm font-semibold text-gray-700 hover:text-gray-900 flex items-center gap-2">
                                <span>🔍 Xem dữ liệu JSON</span>
                                <span className="text-xs text-gray-500">(dành cho debug)</span>
                            </summary>
                            <pre className="mt-2 p-4 bg-gray-900 text-gray-100 rounded-lg overflow-x-auto text-xs">
                                {JSON.stringify(submission, null, 2)}
                            </pre>
                        </details>
                    </section>
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
                    >
                        Đóng
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * Info Item Component - Display label-value pairs
 */
function InfoItem({
    label,
    value,
    mono = false,
    className = ''
}: {
    label: string;
    value: string | number;
    mono?: boolean;
    className?: string;
}) {
    return (
        <div className="flex flex-col">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{label}</span>
            <span className={`text-sm text-gray-900 ${mono ? 'font-mono' : ''} ${className}`}>
                {value || 'N/A'}
            </span>
        </div>
    );
}

/**
 * IndexedDB Helper for caching designs
 */
class DesignCache {
    private dbName = 'AdminDesignsCache';
    private storeName = 'designs';
    private version = 1;
    private db: IDBDatabase | null = null;

    async init(): Promise<void> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.version);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                this.db = request.result;
                resolve();
            };

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;
                if (!db.objectStoreNames.contains(this.storeName)) {
                    db.createObjectStore(this.storeName, { keyPath: 'id' });
                }
            };
        });
    }

    async saveDesigns(designs: Submission[]): Promise<void> {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([this.storeName], 'readwrite');
            const store = transaction.objectStore(this.storeName);

            designs.forEach(design => {
                store.put(design);
            });

            transaction.oncomplete = () => resolve();
            transaction.onerror = () => reject(transaction.error);
        });
    }

    async getAllDesigns(): Promise<Submission[]> {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([this.storeName], 'readonly');
            const store = transaction.objectStore(this.storeName);
            const request = store.getAll();

            request.onsuccess = () => resolve(request.result || []);
            request.onerror = () => reject(request.error);
        });
    }

    async clear(): Promise<void> {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([this.storeName], 'readwrite');
            const store = transaction.objectStore(this.storeName);
            const request = store.clear();

            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }
}

const designCache = new DesignCache();

export default function AdminDashboard() {
    const [submissions, setSubmissions] = useState<Submission[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
    const [loadingImages, setLoadingImages] = useState<{[key: string]: string}>({});

    // Filter and Search states
    const [searchText, setSearchText] = useState('');
    const [filterPrintType, setFilterPrintType] = useState<'all' | 'laser' | 'silk'>('all');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50;

    // Load more states
    const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [totalLoadedFromFirestore, setTotalLoadedFromFirestore] = useState(0);
    const BATCH_SIZE = 2000;

    // Filtered submissions based on search and filters
    const filteredSubmissions = useMemo(() => {
        return submissions.filter(sub => {
            // Search by design ID
            if (searchText.trim()) {
                const designId = (sub.design_id || sub.id).toLowerCase();
                if (!designId.includes(searchText.toLowerCase())) {
                    return false;
                }
            }

            // Filter by print type
            if (filterPrintType !== 'all') {
                if (sub.design_options?.printType !== filterPrintType) {
                    return false;
                }
            }

            // Filter by date range
            if (filterDateFrom || filterDateTo) {
                const subDate = new Date(sub.created_at?.seconds * 1000 || sub.createdAt || 0);

                if (filterDateFrom) {
                    const fromDate = new Date(filterDateFrom);
                    fromDate.setHours(0, 0, 0, 0);
                    if (subDate < fromDate) {
                        return false;
                    }
                }

                if (filterDateTo) {
                    const toDate = new Date(filterDateTo);
                    toDate.setHours(23, 59, 59, 999);
                    if (subDate > toDate) {
                        return false;
                    }
                }
            }

            return true;
        });
    }, [submissions, searchText, filterPrintType, filterDateFrom, filterDateTo]);

    // Calculate pagination
    const totalPages = Math.ceil(filteredSubmissions.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedSubmissions = filteredSubmissions.slice(startIndex, endIndex);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchText, filterPrintType, filterDateFrom, filterDateTo]);

    // Initialize: Load from IndexedDB first, then sync with Firestore
    useEffect(() => {
        let unsubscribe: (() => void) | undefined;

        const initializeData = async () => {
            try {
                // Step 1: Load cached designs from IndexedDB
                console.log('[AdminDashboard] Loading from IndexedDB cache...');
                const cachedDesigns = await designCache.getAllDesigns();
                if (cachedDesigns.length > 0) {
                    console.log(`[AdminDashboard] Found ${cachedDesigns.length} cached designs`);
                    setSubmissions(cachedDesigns);
                    setLoading(false);
                }

                // Step 2: Fetch latest 2000 designs from Firestore
                console.log('[AdminDashboard] Fetching latest designs from Firestore...');
                const q = query(
                    collection(db, "design_submissions"),
                    orderBy("created_at", "desc"),
                    limit(BATCH_SIZE)
                );

                unsubscribe = onSnapshot(q, async (snapshot) => {
                    const data: Submission[] = [];
                    let lastVisible: QueryDocumentSnapshot<DocumentData> | null = null;

                    snapshot.forEach((doc) => {
                        data.push({ id: doc.id, ...doc.data() } as Submission);
                        lastVisible = doc;
                    });

                    setLastDoc(lastVisible);
                    setHasMore(snapshot.docs.length === BATCH_SIZE);
                    setTotalLoadedFromFirestore(data.length);

                    // Merge with cached data (prioritize Firestore for latest data)
                    const mergedData = mergeDesigns(data, cachedDesigns);
                    setSubmissions(mergedData);
                    setLoading(false);

                    // Save to IndexedDB
                    await designCache.saveDesigns(data);
                    console.log(`[AdminDashboard] Loaded ${data.length} designs from Firestore, merged with cache (total: ${mergedData.length})`);
                });
            } catch (error) {
                console.error('[AdminDashboard] Error initializing data:', error);
                setLoading(false);
            }
        };

        initializeData();

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, []);

    // Merge designs: Firestore data takes priority, then add unique cached designs
    const mergeDesigns = (firestoreData: Submission[], cachedData: Submission[]): Submission[] => {
        const firestoreIds = new Set(firestoreData.map(d => d.id));
        const uniqueCached = cachedData.filter(d => !firestoreIds.has(d.id));
        return [...firestoreData, ...uniqueCached].sort((a, b) => {
            const timeA = a.created_at?.seconds || a.createdAt?.seconds || 0;
            const timeB = b.created_at?.seconds || b.createdAt?.seconds || 0;
            return timeB - timeA; // Descending order (newest first)
        });
    };

    // Load more designs (fetch older designs)
    const loadMoreDesigns = async () => {
        if (!hasMore || loadingMore || !lastDoc) return;

        try {
            setLoadingMore(true);
            console.log('[AdminDashboard] Loading more designs...');

            const q = query(
                collection(db, "design_submissions"),
                orderBy("created_at", "desc"),
                startAfter(lastDoc),
                limit(BATCH_SIZE)
            );

            const snapshot = await getDocs(q);
            const newDesigns: Submission[] = [];
            let lastVisible: QueryDocumentSnapshot<DocumentData> | null = null;

            snapshot.forEach((doc) => {
                newDesigns.push({ id: doc.id, ...doc.data() } as Submission);
                lastVisible = doc;
            });

            setLastDoc(lastVisible);
            setHasMore(snapshot.docs.length === BATCH_SIZE);
            setTotalLoadedFromFirestore(prev => prev + newDesigns.length);

            // Merge with existing submissions
            const updatedSubmissions = [...submissions, ...newDesigns];
            setSubmissions(updatedSubmissions);

            // Save to IndexedDB
            await designCache.saveDesigns(newDesigns);

            console.log(`[AdminDashboard] Loaded ${newDesigns.length} more designs (total: ${updatedSubmissions.length})`);
        } catch (error) {
            console.error('[AdminDashboard] Error loading more designs:', error);
            alert('Không thể load thêm designs. Vui lòng thử lại.');
        } finally {
            setLoadingMore(false);
        }
    };

    // Load image from Modal API
    const loadImage = async (imagePath: string, imageKey: string) => {
        // Skip if already loading or successfully loaded (but allow retry on error)
        if (loadingImages[imageKey] === 'loading' || (loadingImages[imageKey] && loadingImages[imageKey] !== 'error')) {
            return;
        }

        try {
            setLoadingImages(prev => ({ ...prev, [imageKey]: 'loading' }));

            // Convert Modal internal path to backend endpoint URL
            const imageUrl = getImageUrl(imagePath);

            console.log(`[AdminDashboard] Loading image: ${imageUrl}`);

            // Fetch image and convert to base64 for display
            const response = await fetch(imageUrl);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const blob = await response.blob();
            const reader = new FileReader();
            reader.onloadend = () => {
                setLoadingImages(prev => ({ ...prev, [imageKey]: reader.result as string }));
                console.log(`[AdminDashboard] ✅ Image loaded: ${imageKey}`);
            };
            reader.onerror = () => {
                throw new Error('FileReader error');
            };
            reader.readAsDataURL(blob);
        } catch (err: any) {
            console.error(`[AdminDashboard] ❌ Failed to load image (${imageKey}):`, err.message);
            setLoadingImages(prev => ({ ...prev, [imageKey]: 'error' }));
        }
    };

    // Helper: Convert Modal internal path to backend /get_image endpoint (for main component)
    const getImageUrl = (imagePath: string): string => {
        if (!imagePath) return '';

        // If already a full URL, return as-is
        if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
            return imagePath;
        }

        // Use backend /get_image endpoint to proxy Modal Volume
        const encodedPath = encodeURIComponent(imagePath);
        const baseUrl = import.meta.env.VITE_MODAL_API_BASE_URL;
        return `${baseUrl}/get_image?path=${encodedPath}`;
    };

    // Format timestamp
    const formatTimestamp = (timestamp: any) => {
        if (!timestamp) return 'N/A';
        if (timestamp.seconds) {
            return new Date(timestamp.seconds * 1000).toLocaleString('vi-VN');
        }
        return new Date(timestamp).toLocaleString('vi-VN');
    };

    // Get product type (support both old and new field names)
    const getProductType = (sub: Submission) => {
        return sub.product_type || sub.productType || 'Unknown';
    };

    // Get created time (support both old and new field names)
    const getCreatedTime = (sub: Submission) => {
        return sub.created_at || sub.createdAt;
    };

    // Get print type display
    const getPrintTypeDisplay = (sub: Submission) => {
        const printType = sub.design_options?.printType;
        if (!printType) return 'N/A';
        return printType === 'laser' ? '🔥 Khắc Laser' : '🎨 In Lụa';
    };

    // Get position display
    const getPositionDisplay = (sub: Submission) => {
        const pos = sub.design_options?.position;
        if (!pos) return 'N/A';
        return `${pos.x > 0 ? '+' : ''}${pos.x}%, ${pos.y > 0 ? '+' : ''}${pos.y}%`;
    };

    // Update submission status
    const updateStatus = async (id: string, newStatus: string) => {
        try {
            const docRef = doc(db, "design_submissions", id);
            await updateDoc(docRef, {
                status: newStatus,
                updated_at: new Date()
            });
        } catch (error) {
            console.error("Error updating status:", error);
            alert("Không thể cập nhật trạng thái");
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 font-sans p-4 sm:p-8">
            <header className="mb-8">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-gray-600 mt-2">Quản lý các thiết kế của khách hàng</p>
            </header>

            {/* Warning Banner + Load More Button */}
            {(totalLoadedFromFirestore >= BATCH_SIZE || hasMore) && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex items-start gap-3">
                        <div className="text-amber-600 mt-0.5">⚠️</div>
                        <div>
                            <p className="text-sm font-semibold text-amber-900">
                                Hiển thị {totalLoadedFromFirestore.toLocaleString()} designs mới nhất từ Firestore
                            </p>
                            <p className="text-xs text-amber-700 mt-1">
                                {submissions.length > totalLoadedFromFirestore && (
                                    <span>Tổng cộng {submissions.toLocaleString()} designs (bao gồm cache). </span>
                                )}
                                {hasMore ? 'Có thể có designs cũ hơn chưa được tải.' : 'Đã tải hết tất cả designs.'}
                            </p>
                        </div>
                    </div>
                    {hasMore && (
                        <button
                            onClick={loadMoreDesigns}
                            disabled={loadingMore}
                            className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium whitespace-nowrap"
                        >
                            {loadingMore ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Đang tải...
                                </>
                            ) : (
                                <>
                                    <ChevronDown className="w-4 h-4" />
                                    Load thêm {BATCH_SIZE.toLocaleString()} designs cũ hơn
                                </>
                            )}
                        </button>
                    )}
                </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Header with title and controls */}
                <div className="p-4 sm:p-6 border-b border-gray-100">
                    <div className="flex justify-between items-center mb-4">
                        <div>
                            <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                                Thiết kế ({filteredSubmissions.length.toLocaleString()}/{submissions.length.toLocaleString()})
                            </h2>
                            {filteredSubmissions.length > 0 && (
                                <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                                    <p>
                                        Hiển thị {(startIndex + 1).toLocaleString()}-{Math.min(endIndex, filteredSubmissions.length).toLocaleString()} / {filteredSubmissions.length.toLocaleString()} • Trang {currentPage}/{totalPages}
                                    </p>
                                    {submissions.length > totalLoadedFromFirestore && (
                                        <p className="text-blue-600">
                                            💾 {(submissions.length - totalLoadedFromFirestore).toLocaleString()} designs từ cache
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                        {loading && <Loader2 className="w-5 h-5 animate-spin text-gray-400" />}
                    </div>

                    {/* Search and Filter Controls */}
                    <div className="space-y-3">
                        {/* Search Input */}
                        <div className="flex items-center gap-2">
                            <Search className="w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Tìm kiếm theo Mã thiết kế..."
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Filters */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {/* Print Type Filter */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Kiểu in</label>
                                <select
                                    value={filterPrintType}
                                    onChange={(e) => setFilterPrintType(e.target.value as 'all' | 'laser' | 'silk')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="all">Tất cả</option>
                                    <option value="laser">🔥 Khắc Laser</option>
                                    <option value="silk">🎨 In Lụa</option>
                                </select>
                            </div>

                            {/* Date From Filter */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Từ ngày</label>
                                <input
                                    type="date"
                                    value={filterDateFrom}
                                    onChange={(e) => setFilterDateFrom(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            {/* Date To Filter */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Đến ngày</label>
                                <input
                                    type="date"
                                    value={filterDateTo}
                                    onChange={(e) => setFilterDateTo(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>

                        {/* Reset Filters Button */}
                        {(searchText || filterPrintType !== 'all' || filterDateFrom || filterDateTo) && (
                            <button
                                onClick={() => {
                                    setSearchText('');
                                    setFilterPrintType('all');
                                    setFilterDateFrom('');
                                    setFilterDateTo('');
                                }}
                                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                            >
                                ✕ Xóa bộ lọc
                            </button>
                        )}
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs sm:text-sm">
                        <thead className="bg-gray-50 text-gray-500 font-medium">
                            <tr>
                                <th className="px-3 sm:px-6 py-3 sm:py-4">Mã</th>
                                <th className="px-3 sm:px-6 py-3 sm:py-4">Thời gian</th>
                                <th className="px-3 sm:px-6 py-3 sm:py-4">Sản phẩm</th>
                                <th className="px-3 sm:px-6 py-3 sm:py-4">Kiểu in</th>
                                <th className="px-3 sm:px-6 py-3 sm:py-4">Trạng thái</th>
                                <th className="px-3 sm:px-6 py-3 sm:py-4">Thao tác</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {paginatedSubmissions.map((sub) => (
                                <tr key={sub.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-3 sm:px-6 py-3 sm:py-4 font-mono text-xs text-gray-500">
                                        {sub.design_id || sub.id.slice(0, 12)}
                                    </td>
                                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-gray-600 text-xs sm:text-sm">
                                        {formatTimestamp(getCreatedTime(sub))}
                                    </td>
                                    <td className="px-3 sm:px-6 py-3 sm:py-4 capitalize font-medium text-gray-900">
                                        {getProductType(sub).replace('_', ' ')}
                                    </td>
                                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-gray-600">
                                        {getPrintTypeDisplay(sub)}
                                    </td>
                                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                                        <select
                                            value={sub.status}
                                            onChange={(e) => updateStatus(sub.id, e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            className={`text-xs font-medium rounded-full px-3 py-1.5 border-none cursor-pointer focus:ring-2 focus:ring-offset-1 transition-all appearance-none pr-8 bg-no-repeat bg-[right_0.5rem_center] bg-[length:1em] ${
                                                sub.status === 'completed' ? 'bg-green-100 text-green-800 focus:ring-green-500' :
                                                sub.status === 'processing' ? 'bg-blue-100 text-blue-800 focus:ring-blue-500' :
                                                'bg-yellow-100 text-yellow-800 focus:ring-yellow-500'
                                            }`}
                                            style={{
                                                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`
                                            }}
                                        >
                                            <option value="pending_charge">Chờ xử lý</option>
                                            <option value="processing">Đang xử lý</option>
                                            <option value="completed">Hoàn thành</option>
                                        </select>
                                    </td>
                                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                                        <button
                                            onClick={() => setSelectedSubmission(sub)}
                                            className="flex items-center gap-1 px-2 sm:px-3 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition-colors text-xs font-medium"
                                        >
                                            <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                                            <span className="hidden sm:inline">Xem</span>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {paginatedSubmissions.length === 0 && !loading && (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-gray-400">
                                        {submissions.length === 0
                                            ? 'Chưa có thiết kế nào. Hãy tạo thiết kế đầu tiên!'
                                            : 'Không tìm thấy thiết kế phù hợp với bộ lọc.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                    <div className="p-4 sm:p-6 border-t border-gray-100 bg-gray-50">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                            {/* Page Info */}
                            <div className="text-sm text-gray-600">
                                Trang <span className="font-semibold">{currentPage}</span> / <span className="font-semibold">{totalPages}</span>
                            </div>

                            {/* Pagination Buttons */}
                            <div className="flex items-center gap-2">
                                {/* First Page */}
                                <button
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                    className="px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    ««
                                </button>

                                {/* Previous Page */}
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    « Trước
                                </button>

                                {/* Page Numbers */}
                                <div className="flex items-center gap-1">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => setCurrentPage(pageNum)}
                                                className={`w-10 h-10 text-sm font-medium rounded-lg transition-colors ${
                                                    currentPage === pageNum
                                                        ? 'bg-blue-600 text-white'
                                                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                                }`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Next Page */}
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Sau »
                                </button>

                                {/* Last Page */}
                                <button
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    »»
                                </button>
                            </div>

                            {/* Jump to Page */}
                            <div className="flex items-center gap-2">
                                <label className="text-sm text-gray-600">Đến trang:</label>
                                <input
                                    type="number"
                                    min="1"
                                    max={totalPages}
                                    value={currentPage}
                                    onChange={(e) => {
                                        const page = parseInt(e.target.value);
                                        if (page >= 1 && page <= totalPages) {
                                            setCurrentPage(page);
                                        }
                                    }}
                                    className="w-16 px-2 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Detail Modal */}
            {selectedSubmission && (
                <SubmissionDetailModal 
                    submission={selectedSubmission} 
                    onClose={() => setSelectedSubmission(null)}
                    loadingImages={loadingImages}
                    onLoadImage={loadImage}
                />
            )}
        </div>
    );
}
